import {match, Pattern} from 'ts-pattern';

import {useRef, useState} from 'react';

import {isNilOrEmpty} from 'ramda-adjunct';

import {type Nullish, useDebouncedCallback} from 'shared/web';

/**
 * Typing into the page input commits per change, so typing a multi-digit page
 * ("1" then "12") would otherwise fire a data fetch per digit. Matches the
 * spirit of the 250ms filter debounce in ColumnMenuFilterTab.
 */
const PAGE_INPUT_DEBOUNCE_MS = 300;

/**
 * Draft-string page-input state with debounced, clamped commits for the INPUT
 * pagination variant. Operates entirely in 1-BASED page space (the store page
 * is 0-based; GridPagination converts at the boundary).
 *
 * Draft model: `draft === null` means "not editing" — the input displays the
 * committed page from the store. While editing, the draft may be EMPTY (`''`)
 * so the user can delete the current page and type a new one; empty is never
 * committed, and blurring an empty input snaps back to the committed page.
 *
 * Commit model: numeric drafts are clamped to [1, totalPages] and committed via
 * a 300ms debounce. The debounced commit reads the draft from a ref at fire
 * time, so clearing the field cancels a pending commit (useDebouncedCallback
 * has no cancel API). Blur flushes a differing draft immediately.
 *
 * The draft is auto-cleared during render once the committed page catches up
 * (React setState-during-render bail-out) — external page changes (filter reset
 * to page 1) are therefore reflected whenever the user is not mid-edit.
 */
export function useDebouncedPageInput(
  currentPageOneBased: number,
  totalPages: number,
  onPageChange: (pageOneBased: number) => void
) {
  // null = not editing; '' = user deleted the value; otherwise digits.
  const [draft, setDraft] = useState<string | null>(null);
  // Handler-synced mirror of the draft, read by the debounced commit at fire
  // time. Nulled on blur-flush so a pending timer cannot double-commit.
  const draftRef = useRef<string | null>(null);

  // In-render adjustment: once the committed page equals the draft (our own
  // commit landed), drop the draft so the display follows the store again.
  if (draft !== null && draft !== '' && Number(draft) === currentPageOneBased) {
    setDraft(null);
  }

  const clampPage = (page: number) => Math.min(Math.max(page, 1), totalPages);

  const commitDraft = () => {
    const text = draftRef.current;
    if (isNilOrEmpty(text)) {
      return;
    }
    const page = Number(text);
    // Same-page commits must be dropped (mirrors the blur guard): the grid's
    // handlePageChange runs deselectAll() unconditionally, so committing the
    // page already shown (typing the current page, next+prev within the
    // debounce window, or "999" clamping back on the last page) would wipe the
    // user's row selection without navigating anywhere.
    if (page === currentPageOneBased) {
      return;
    }
    onPageChange(page);
  };
  const debouncedCommit = useDebouncedCallback(commitDraft, PAGE_INPUT_DEBOUNCE_MS);

  const applyDraft = (text: string) => {
    setDraft(text);
    draftRef.current = text;
  };

  const handleInputChange = (value: string | Nullish) => {
    const text = value ?? '';
    match(text)
      // Deleting everything is allowed — keep the field empty, commit nothing.
      // A previously scheduled commit is cancelled by the emptied ref.
      .with('', () => applyDraft(''))
      // Non-integer garbage (paste, exotic keyboards): ignore, keep the draft.
      .with(
        Pattern.when((entry) => !Number.isInteger(Number(entry))),
        () => undefined
      )
      .otherwise((entry) => {
        applyDraft(String(clampPage(Number(entry))));
        debouncedCommit();
      });
  };

  const handleInputBlur = () => {
    const text = draftRef.current;
    draftRef.current = null;
    if (isNilOrEmpty(text)) {
      // Empty or untouched: snap the display back to the committed page.
      setDraft(null);
      return;
    }
    const page = Number(text);
    if (page === currentPageOneBased) {
      setDraft(null);
      return;
    }
    // Flush immediately on blur; the pending debounce timer no-ops on the
    // nulled ref. The draft stays visible until the committed page catches up
    // (cleared by the in-render adjustment above).
    onPageChange(page);
  };

  // Displayed value and the page used for arrow stepping / bounds checks.
  const value = draft ?? String(currentPageOneBased);
  const displayedPage = isNilOrEmpty(draft) ? currentPageOneBased : Number(draft);

  const handleStep = (delta: number) => {
    applyDraft(String(clampPage(displayedPage + delta)));
    debouncedCommit();
  };

  const handlePrevPage = () => handleStep(-1);
  const handleNextPage = () => handleStep(1);

  return {value, displayedPage, handleInputChange, handleInputBlur, handlePrevPage, handleNextPage};
}
